/* eslint-disable max-len */
import React from 'react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { LoginView } from '../../views/authentication/login'
import { ChatView } from '../../views/chat'
import { Root } from '../../views/root'
import { SettingsView } from '../../views/settings'
import { SocietyView } from '../../views/society/SocietyView'
import { UserView } from '../../views/user'
import { AuthenticatedRoute } from './AuthenticatedRoute'
import { NotFoundView } from '../../views/error/NotFound'
import { useSocietyRoutes } from '../society/hooks/useSocietyRoutes'
import { SignUpView } from '../../views/authentication/signup'
import { HamburgerContextProvider } from '../../hooks/useHamburgerContext'
import { RouteErrorView } from '../../views/error/RouteErrorView'
import { FindSocietyView } from '../../views/society/FindSocietyView'
import { FindSocietyViewConfirmActivationView } from '../../views/society/FindSocietyConfirmActivationView'
import { CreateSocietyView } from '../../views/society/CreateSocietyView'
import { ActivatedSocietyConfirmationView } from '../../views/society/ActivatedSocietyConfirmationView'
import { CreateSocietyTypeSelectionView } from '../../views/society/CreateSocietyTypeSelectionView'
import { SocietyInviteBase } from '../../components/society/SocietyInvite'
import { ResetPasswordView } from '../../views/authentication/resetPassword'
import { PostPrintView, PostView } from '../../views/post'
import { VerifyEmailView } from '../../views/authentication/verifyEmail'
import { RetrievePasswordView } from '../../views/authentication/retrievePassword'
import { ConfirmEmailChangeView } from '../../views/authentication/confirmEmailChange'
import { LoginRoute } from './LoginRoute'
import { SuperAdminView } from '../../views/admin/SuperAdminView'
import { SuperAdminRoute } from './SuperAdminRoute'
import { SelectedSocietyContextProvider } from '../../hooks/useSelectedSocietyContext'
import { FeedView } from '../../views/feed/FeedView'
import { NewSocietyView } from '../../views/society/NewSocietyView'

const AppRoutes = (): JSX.Element | null => {
  const societyRoutes = useSocietyRoutes()
  const router = createBrowserRouter([
    {
      path: '/login',
      element: (
        <LoginRoute>
          <LoginView />
        </LoginRoute>
      ),
      errorElement: <RouteErrorView />,
    },
    {
      path: '/signup',
      element: <SignUpView />,
      errorElement: <RouteErrorView />,
    },
    {
      path: '/reset-password',
      element: <ResetPasswordView />,
      errorElement: <RouteErrorView />,
    },
    {
      path: '/retrieve-password',
      element: <RetrievePasswordView />,
      errorElement: <RouteErrorView />,
    },
    {
      path: '/verify-email',
      element: <VerifyEmailView />,
      errorElement: <RouteErrorView />,
    },
    {
      // Where the "confirm your new email address" link lands. Unauthenticated
      // on purpose: it is opened wherever the new inbox is read, which is
      // routinely a different browser from the one that asked for the change.
      path: '/confirm-email-change',
      element: <ConfirmEmailChangeView />,
      errorElement: <RouteErrorView />,
    },
    {
      // Where the mailed link lands. Kept as its own path because it is baked
      // into mails already sent and into the universal-link configuration the
      // apps ship with, and it is the same page: the token in the query is what
      // turns it into the set-password half.
      path: '/email-verification',
      element: <VerifyEmailView />,
      errorElement: <RouteErrorView />,
    },
    {
      path: '/',
      element: <Navigate to="/feed" replace />,
    },
    {
      element: (
        <AuthenticatedRoute>
          <SelectedSocietyContextProvider>
            <HamburgerContextProvider>
              <SocietyInviteBase>
                <Root />
              </SocietyInviteBase>
            </HamburgerContextProvider>
          </SelectedSocietyContextProvider>
        </AuthenticatedRoute>
      ),
      errorElement: <RouteErrorView />,
      children: [
        {
          path: 'feed',
          element: <FeedView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'admin/*',
          element: (
            <SuperAdminRoute>
              <SuperAdminView />
            </SuperAdminRoute>
          ),
          errorElement: <RouteErrorView />,
        },
        {
          path: 'new-society',
          element: <NewSocietyView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'find-society',
          element: <FindSocietyView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'create-society',
          element: <CreateSocietyView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'post/:id',
          element: <PostView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'post/:id/print',
          element: <PostPrintView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'create-society/pick-type',
          element: <CreateSocietyTypeSelectionView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'activated-society',
          element: <ActivatedSocietyConfirmationView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'find-society/confirm-activation/:id',
          element: <FindSocietyViewConfirmActivationView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'society',
          element: <SocietyView />,
          children: societyRoutes,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'user/:id/society/:societyId',
          element: <UserView />,
          errorElement: <RouteErrorView />,
        },
        {
          path: 'chat',
          children: [
            {
              index: true,
              element: <ChatView />,
            },
            {
              path: ':id',
              element: <ChatView />,
            },
          ],
          errorElement: <RouteErrorView />,
        },
        {
          path: 'settings/*',
          element: <SettingsView />,
          errorElement: <RouteErrorView />,
        },
      ],
    },
    {
      path: 'not-found',
      element: (
        <AuthenticatedRoute>
          <NotFoundView />
        </AuthenticatedRoute>
      ),
      errorElement: <RouteErrorView />,
    },
    {
      path: '*',
      element: <Navigate to="/not-found" replace />,
    },
  ])

  return <RouterProvider router={router} />
}

AppRoutes.displayName = 'AppRoutes'
export { AppRoutes }
